/**
 * Offline queue for field capture. Everything the phone records is written here
 * first and removed only after the server has stored it, so closing the tab,
 * losing signal or a server error never loses a GPS point or an image.
 * Each item carries a client_event_uuid; the server ignores repeats.
 */

export type Store = "gps" | "frames" | "videos" | "controls";
const DB_NAME = "roadricsai-field";
const STORES: Store[] = ["gps", "frames", "videos", "controls"];

export interface GpsItem { id: string; survey_uuid: string; payload: Record<string, unknown>; at: string }
export interface MediaItem { id: string; survey_uuid: string; meta: Record<string, string>; blob: Blob; filename: string; at: string }
export interface ControlItem { id: string; survey_uuid: string; url: string; body: Record<string, unknown>; at: string; phase: "start" | "stop" }

let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  dbp ??= new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => STORES.forEach((s) => r.result.objectStoreNames.contains(s) || r.result.createObjectStore(s, { keyPath: "id" }));
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
  return dbp;
}

async function tx<T>(store: Store, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => resolve(req ? (req as IDBRequest<T>).result : undefined);
    t.onerror = () => reject(t.error);
  });
}

export const put = (store: Store, item: object) => tx(store, "readwrite", (s) => { s.put(item); });
export const remove = (store: Store, id: string) => tx(store, "readwrite", (s) => { s.delete(id); });
export const all = async <T>(store: Store) => ((await tx<T[]>(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>)) ?? []);
export const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

export async function pendingCounts(surveyUuid?: string) {
  const out: Record<Store, number> = { gps: 0, frames: 0, videos: 0, controls: 0 };
  for (const s of STORES) {
    const items = await all<{ survey_uuid: string }>(s);
    out[s] = surveyUuid ? items.filter((i) => i.survey_uuid === surveyUuid).length : items.length;
  }
  return { ...out, total: out.gps + out.frames + out.videos + out.controls };
}

export async function storageUse(): Promise<{ usage: number; quota: number }> {
  try {
    const e = await navigator.storage.estimate();
    return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
  } catch {
    return { usage: 0, quota: 0 };
  }
}

/* ---------------------------------------------------------------- sending */

async function send(url: string, init: RequestInit): Promise<unknown> {
  const headers = new Headers(init.headers);
  headers.set("x-rai-client", "web");
  const r = await fetch(url, { ...init, headers, credentials: "same-origin" });
  if (r.status === 401) throw new SyncError("signed_out", "Your sign-in has expired. Sign in again to finish syncing; nothing on this phone is lost.");
  if (!r.ok) {
    let msg = `Server error ${r.status}`;
    try { const j = await r.json(); msg = typeof j.detail === "string" ? j.detail : msg; } catch { /* keep default */ }
    // 4xx other than 401/408/429 won't succeed on retry: report it, keep the item.
    throw new SyncError(r.status >= 500 || r.status === 408 || r.status === 429 ? "retry" : "rejected", msg);
  }
  return r.json();
}

export class SyncError extends Error {
  kind: "signed_out" | "retry" | "rejected";
  constructor(kind: SyncError["kind"], message: string) {
    super(message);
    this.kind = kind;
  }
}

export const sendGps = (items: GpsItem[]) =>
  send("/api/field/gps", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ points: items.map((i) => i.payload) }) }) as Promise<{ results: Record<string, unknown>[]; latest: Record<string, unknown> | null }>;

export function sendMedia(url: string, item: MediaItem, field: string) {
  const fd = new FormData();
  Object.entries(item.meta).forEach(([k, v]) => fd.append(k, v));
  fd.append(field, item.blob, item.filename);
  return send(url, { method: "POST", body: fd }) as Promise<Record<string, unknown>>;
}

export const sendControl = (c: ControlItem) =>
  send(c.url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(c.body) }) as Promise<Record<string, unknown>>;

let syncing: Promise<SyncResult> | null = null;
export interface SyncResult { gps: number; frames: number; videos: number; controls: number; error?: SyncError }

/** Sends everything queued, oldest first: session starts, GPS, images, video, then survey ends. */
export function syncAll(onGps?: (latest: Record<string, unknown> | null) => void): Promise<SyncResult> {
  syncing ??= (async () => {
    const done: SyncResult = { gps: 0, frames: 0, videos: 0, controls: 0 };
    try {
      const controls = (await all<ControlItem>("controls")).sort((a, b) => a.at.localeCompare(b.at));
      for (const c of controls.filter((x) => x.phase === "start")) { await sendControl(c); await remove("controls", c.id); done.controls++; }

      const gps = (await all<GpsItem>("gps")).sort((a, b) => a.at.localeCompare(b.at));
      for (let i = 0; i < gps.length; i += 200) {
        const batch = gps.slice(i, i + 200);
        const r = await sendGps(batch);
        for (const g of batch) await remove("gps", g.id);
        done.gps += batch.length;
        onGps?.(r.latest);
      }
      for (const f of (await all<MediaItem>("frames")).sort((a, b) => a.at.localeCompare(b.at))) {
        await sendMedia("/api/field/frames", f, "image"); await remove("frames", f.id); done.frames++;
      }
      for (const v of (await all<MediaItem>("videos")).sort((a, b) => a.at.localeCompare(b.at))) {
        await sendMedia("/api/field/video", v, "video"); await remove("videos", v.id); done.videos++;
      }
      for (const c of (await all<ControlItem>("controls")).filter((x) => x.phase === "stop").sort((a, b) => a.at.localeCompare(b.at))) {
        await sendControl(c); await remove("controls", c.id); done.controls++;
      }
    } catch (e) {
      done.error = e instanceof SyncError ? e : new SyncError("retry", e instanceof Error ? e.message : String(e));
    }
    return done;
  })().finally(() => { syncing = null; });
  return syncing;
}
